import { useEffect, useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ShieldCheck,
  MapPin,
  Truck,
  Clock,
  CreditCard,
  Headphones,
  Package,
  Store,
  Star,
  Zap,
  Gift,
  Heart,
} from "lucide-react";
import { ENDPOINTS } from "../../config";

interface HomeKpiItem {
  title: string;
  subtitle: string;
  subtitleLine2?: string;
  icon?: string;
}

const ICON_MAP: Record<string, LucideIcon> = {
  "shield-check": ShieldCheck,
  "map-pin": MapPin,
  truck: Truck,
  clock: Clock,
  "credit-card": CreditCard,
  headphones: Headphones,
  package: Package,
  store: Store,
  star: Star,
  zap: Zap,
  gift: Gift,
  heart: Heart,
};

const DEFAULT_KPIS = [
  {
    title: "Secure Payments",
    subtitle: "M-Pesa · Visa ·",
    subtitleLine2: "Mastercard",
    icon: "shield-check",
  },
  { title: "Click & Collect", subtitle: "30+ stores nationwide", subtitleLine2: "", icon: "map-pin" },
  { title: "1-Day Delivery", subtitle: "Free in Nairobi", subtitleLine2: "", icon: "truck" },
  {
    title: "Countryside Shipping",
    subtitle: "2–5 days, fully tracked",
    subtitleLine2: "",
    icon: "clock",
  },
  {
    title: "Pay with Mosmos",
    subtitle: "Interest-free instalments",
    subtitleLine2: "",
    icon: "credit-card",
  },
  { title: "24/7 Support", subtitle: "Real humans, real help", subtitleLine2: "", icon: "headphones" },
];

function getGridClass(count: number): string {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count === 3) return "grid-cols-2 sm:grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  if (count === 5) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5";
  if (count === 6) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6";
  if (count === 7) return "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7";
  return "grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8";
}

export function TrustTicker() {
  const [items, setItems] = useState<HomeKpiItem[]>(DEFAULT_KPIS);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(ENDPOINTS.HOME_KPIS.PUBLIC);
        const data = await res.json();
        if (data?.isOk && Array.isArray(data.data) && data.data.length) {
          setItems(data.data);
        }
      } catch (error) {
        console.error("Failed to load home KPIs:", error);
      }
    };
    load();
  }, []);

  const gridClass = useMemo(() => getGridClass(items.length), [items.length]);

  return (
    <section className="border-y border-border bg-surface/40 backdrop-blur-sm py-2.5">
      <div
        className={`container mx-auto px-4 lg:px-8 grid ${gridClass} gap-4 lg:gap-3 items-start`}
      >
        {items.map((item) => {
          const Icon = ICON_MAP[item.icon || ""] || ShieldCheck;
          const hasLine2 = Boolean(item.subtitleLine2?.trim());

          return (
            <div
              key={`${item.title}-${item.subtitle}`}
              className={`flex gap-2.5 min-w-0 ${hasLine2 ? "items-start" : "items-center"}`}
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-brand/10 border border-primary/20 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground leading-tight truncate">
                  {item.title}
                </div>
                <div className="text-xs text-muted-foreground leading-snug line-clamp-2">
                  {item.subtitle}
                  {hasLine2 && (
                    <>
                      <br />
                      {item.subtitleLine2}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
